import React, { useState, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useLocation, useParams, Outlet } from "react-router-dom";
import axios from "axios";
import CustomerDashboardView from "../../../components/customer/dashboard/CustomerDashboardView";
import PartnerDashboardView from "../../../components/partner/dashboard/PartnerDashboardView";
import {
  FiPlusCircle,
  FiTrendingUp,
  FiFileText,
  FiMessageCircle,
  FiArrowRight,
  FiMapPin,
  FiGrid,
  FiMaximize,
  FiThumbsUp,
  FiMessageSquare,
  FiShare2,
  FiKey,
  FiTag,
  FiHome,
  FiCompass,
  FiPlus,
  FiCheckCircle,
  FiGift,
  FiUsers,
  FiShoppingBag,
  FiCalendar,
  FiActivity,
  FiAward,
  FiMail,
  FiZap,
  FiLayers,
} from "react-icons/fi";
import SaaSStatCard from "../../../components/common/saas/SaaSStatCard";
import SaaSEmptyState from "../../../components/common/saas/SaaSEmptyState";
import SaaSLoadingSkeleton from "../../../components/common/saas/SaaSLoadingSkeleton";
import SaaSSectionHeader from "../../../components/common/saas/SaaSSectionHeader";
import "../../../components/common/saas/SaaSUIToolkit.css";
import "./MainDashboard.css";

const PropertyCard = ({ item, assetUrl, routeBase, navigate }) => {
  const imageSrc =
    item.propertyImages?.[0]?.image_url
      ? (item.propertyImages[0].image_url.startsWith("http")
          ? item.propertyImages[0].image_url
          : `${assetUrl}${item.propertyImages[0].image_url}`)
      : `https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=600&auto=format&fit=crop&q=80`;

  const budgetDisplay = item.budget ? `₹${Number(item.budget).toLocaleString("en-IN")}` : "Price on Request";

  return (
    <div
      className="saas-property-card"
      onClick={() => navigate(`${routeBase}/properties/${item.id || ""}`)}
    >
      <div className="saas-property-card__img-wrap">
        <img src={imageSrc} alt={item.title || "Property"} loading="lazy" />
        <span className="saas-property-card__badge-purpose">
          {item.purpose_name || item.property_purpose_name || "For Sale"}
        </span>
        <span className="saas-property-card__badge-price">{budgetDisplay}</span>
      </div>
      <div className="saas-property-card__body">
        <h3 className="saas-property-card__title">{item.title || item.property_name || "Modern Residential Property"}</h3>
        <p className="saas-property-card__location">
          <FiMapPin size={13} className="text-primary flex-shrink-0" />
          <span>
            {[item.area_name, item.city_name, item.state_name].filter(Boolean).join(", ") || "Prime Location"}
          </span>
        </p>
        <div className="saas-property-card__specs">
          {item.type_name && (
            <span className="saas-spec-pill">
              <FiGrid size={12} /> {item.type_name}
            </span>
          )}
          {item.sub_type_name && (
            <span className="saas-spec-pill">
              <FiMaximize size={12} /> {item.sub_type_name}
            </span>
          )}
          <span className="saas-spec-pill saas-spec-pill--highlight">
            <FiCheckCircle size={12} /> Verified
          </span>
        </div>
      </div>
    </div>
  );
};

const PostCard = ({ item, routeBase, navigate }) => {
  const authorName =
    item.customer_name ||
    [item.customer?.first_name, item.customer?.last_name].filter(Boolean).join(" ") ||
    [item.partner?.first_name, item.partner?.last_name].filter(Boolean).join(" ") ||
    "Community Partner";

  const initial = authorName.charAt(0).toUpperCase() || "A";
  const serviceName = item.service || item.service_name || "Real Estate";
  const locationText = [item.city_name, item.state_name].filter(Boolean).join(", ");

  return (
    <div
      className="saas-post-card"
      onClick={() => navigate(`${routeBase}/posts/${item.id || ""}`)}
    >
      <div className="saas-post-card__header">
        <div className="saas-post-card__avatar">{initial}</div>
        <div className="saas-post-card__meta">
          <h4 className="saas-post-card__author">{authorName}</h4>
          <span className="saas-post-card__tag">{serviceName}</span>
        </div>
      </div>
      <div className="saas-post-card__body">
        <p className="saas-post-card__desc">
          {item.description ? item.description.replace(/<[^>]+>/g, "").substring(0, 140) : "Looking for reliable verified property insights in the area..."}
          {item.description && item.description.length > 140 ? "..." : ""}
        </p>
        {locationText && (
          <div className="saas-post-card__location">
            <FiMapPin size={12} /> {locationText}
          </div>
        )}
      </div>
      <div className="saas-post-card__footer">
        <div className="d-flex align-items-center gap-3 text-muted" style={{ fontSize: "0.8125rem" }}>
          <span className="d-flex align-items-center gap-1">
            <FiThumbsUp size={13} /> {item.like_count || 0}
          </span>
          <span className="d-flex align-items-center gap-1">
            <FiMessageSquare size={13} /> {item.comment_count || 0}
          </span>
        </div>
        <button
          type="button"
          className="saas-btn saas-btn--outline saas-btn--sm"
          onClick={(e) => {
            e.stopPropagation();
            navigate(`${routeBase}/posts/${item.id || ""}`);
          }}
        >
          <span>View Post</span>
          <FiArrowRight size={12} />
        </button>
      </div>
    </div>
  );
};

const PromoCard = ({ item, assetUrl, routeBase, navigate }) => {
  const imageSrc =
    item.propertyImages?.[0]?.image_url
      ? (item.propertyImages[0].image_url.startsWith("http")
          ? item.propertyImages[0].image_url
          : `${assetUrl}${item.propertyImages[0].image_url}`)
      : item.image_url || `https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=600&auto=format&fit=crop&q=80`;

  return (
    <div
      className="saas-promo-card"
      onClick={() => navigate(`${routeBase}/promotions/${item.id || ""}`)}
    >
      <div className="saas-promo-card__img-wrap">
        <img src={imageSrc} alt={item.title || "Special Promotion"} loading="lazy" />
        <span className="saas-promo-card__tag">
          <FiTag size={12} /> Special Offer
        </span>
      </div>
      <div className="saas-promo-card__body">
        <h3 className="saas-promo-card__title">{item.title || item.promotion_name || "Exclusive Member Promotion"}</h3>
        <p className="saas-promo-card__desc">
          {item.description ? item.description.replace(/<[^>]+>/g, "").substring(0, 110) : "Special featured discount and high-visibility listing campaign."}...
        </p>
        <div className="d-flex align-items-center justify-content-between mt-auto pt-2 border-top">
          <span className="text-primary fw-bold" style={{ fontSize: "0.8125rem" }}>Limited Time</span>
          <span className="saas-link-arrow">
            Explore <FiArrowRight size={13} />
          </span>
        </div>
      </div>
    </div>
  );
};

export const MainDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [greeting, setGreeting] = useState("");
  const loggedInUser = useSelector((state) => state.auth.user);
  const token = loggedInUser?.refreshToken || loggedInUser?.token || loggedInUser?.accessToken;
  const baseUrl = process.env.REACT_APP_BASE_URL;
  const assetUrl = process.env.REACT_APP_ASSET_URL || "";

  const [properties, setProperties] = useState([]);
  const [posts, setPosts] = useState([]);
  const [promotions, setPromotions] = useState([]);
  const [loading, setLoading] = useState(true);

  // Derive active route base (/customer, /partner, or /admin)
  const routeBase = useMemo(() => {
    if (location.pathname.startsWith("/partner")) return "/partner";
    if (location.pathname.startsWith("/admin")) return "/admin";
    return "/customer";
  }, [location.pathname]);

  const userType = loggedInUser?.user?.userType?.name || loggedInUser?.userType || "USER";
  const isPartner = userType === "PARTNER" || location.pathname.startsWith("/partner");
  const isAdmin = userType === "SUPER_ADMIN" || userType === "ALL_HOME_DESIRE" || location.pathname.startsWith("/admin");

  useEffect(() => {
    const hour = new Date().getHours();
    if (hour < 12) setGreeting("Good Morning");
    else if (hour < 18) setGreeting("Good Afternoon");
    else setGreeting("Good Evening");
  }, []);

  useEffect(() => {
    const fetchDashboardData = async () => {
      setLoading(true);
      try {
        const headers = token ? { Authorization: `Bearer ${token}` } : {};

        // 1. Properties Fetch
        try {
          const propRes = await axios.post(
            `${baseUrl}/property/list/preferred/v1`,
            { page_number: 1, page_size: 10, order_by: "DESC", keyword_search: "" },
            { headers }
          );
          const propList = propRes.data?.propertyAssets || propRes.data?.properties || [];
          if (propList.length > 0) {
            setProperties(propList);
          } else {
            // Fallback to standard property list
            const fallbackPropRes = await axios.post(
              `${baseUrl}/property/list`,
              { page_number: 1, page_size: 10, order_by: "DESC" },
              { headers }
            );
            setProperties(fallbackPropRes.data?.properties || fallbackPropRes.data?.propertyAssets || []);
          }
        } catch (propErr) {
          try {
            const fallbackPropRes = await axios.post(
              `${baseUrl}/property/list`,
              { page_number: 1, page_size: 10, order_by: "DESC" },
              { headers }
            );
            setProperties(fallbackPropRes.data?.properties || fallbackPropRes.data?.propertyAssets || []);
          } catch (e) {
            console.error("Error fetching properties:", e);
          }
        }

        // 2. Posts Fetch (Try /property/leads first as used in PostListComponent & Dashboard, then /post/list/preferred)
        try {
          const postRes = await axios.post(
            `${baseUrl}/property/leads`,
            { page_number: 1, page_size: 10, order_by: "DESC" },
            { headers }
          );
          const leadList = postRes.data?.leads || postRes.data?.data || [];
          if (leadList.length > 0) {
            setPosts(leadList);
          } else {
            // Fallback to preferred posts
            const prefPostRes = await axios.post(
              `${baseUrl}/post/list/preferred`,
              { service: "", state: "", city: "", keyword_search: "", page_number: 1, page_size: 10, order_by: "DESC" },
              { headers }
            );
            setPosts(prefPostRes.data?.postAssets || prefPostRes.data?.leads || []);
          }
        } catch (postErr) {
          try {
            const prefPostRes = await axios.post(
              `${baseUrl}/post/list/preferred`,
              { service: "", state: "", city: "", keyword_search: "", page_number: 1, page_size: 10, order_by: "DESC" },
              { headers }
            );
            setPosts(prefPostRes.data?.postAssets || prefPostRes.data?.leads || []);
          } catch (e) {
            console.error("Error fetching posts:", e);
          }
        }

        // 3. Promotions Fetch
        try {
          const promoEndpoint = isPartner ? `${baseUrl}/promotion/list/preferred` : `${baseUrl}/promotion/`;
          const promoPayload = isPartner
            ? { page_number: 1, page_size: 6, order_by: "DESC", is_partner: true }
            : { page_number: 1, page_size: 10, order_by: "DESC" };
          const promoRes = await axios.post(promoEndpoint, promoPayload, { headers });
          setPromotions(
            promoRes.data?.promotions ||
            promoRes.data?.data ||
            promoRes.data?.propertyAssets ||
            []
          );
        } catch (promoErr) {
          console.error("Error fetching promotions:", promoErr);
        }
      } catch (error) {
        console.error("Error in dashboard fetch:", error);
      } finally {
        setLoading(false);
      }
    };

    if (token) {
      fetchDashboardData();
    } else {
      setLoading(false);
    }
  }, [token, baseUrl, isPartner]);

  const [userProfile, setUserProfile] = useState(null);

  const currentUserId =
    loggedInUser?.user?.id ||
    loggedInUser?.id ||
    loggedInUser?.userDetails?.id;

  // Fetch full user profile if name is not available
  useEffect(() => {
    if (currentUserId && token) {
      axios
        .get(`${baseUrl}/admin/user/id/${currentUserId}`, {
          headers: { Authorization: `Bearer ${token}` },
        })
        .then((res) => {
          if (res.data?.user) {
            setUserProfile(res.data.user);
          } else if (res.data) {
            setUserProfile(res.data);
          }
        })
        .catch((err) => console.error("Error fetching user profile in dashboard:", err));
    }
  }, [currentUserId, token, baseUrl]);

  const userObj = userProfile || loggedInUser?.user || loggedInUser;
  const rawFirstName = userObj?.first_name || userObj?.firstName || userObj?.userDetails?.first_name;
  const rawLastName = userObj?.last_name || userObj?.lastName || userObj?.userDetails?.last_name;
  const rawName = userObj?.name || userObj?.customer_name || userObj?.userDetails?.name;
  const fullName = [rawFirstName, rawLastName].filter(Boolean).join(" ") || rawName;
  const emailVal = userObj?.email_address || userObj?.email || userObj?.email_id;
  const emailPrefix = emailVal ? emailVal.split("@")[0] : null;
  const mobileVal = userObj?.mobile_number || userObj?.mobile;

  let displayName = "User";
  if (fullName && fullName.trim() && !fullName.toLowerCase().includes("null")) {
    displayName = fullName.trim();
  } else if (emailPrefix && !emailPrefix.toLowerCase().includes("null")) {
    displayName = emailPrefix.charAt(0).toUpperCase() + emailPrefix.slice(1);
  } else if (mobileVal) {
    displayName = mobileVal;
  } else if (isPartner) {
    displayName = "Partner";
  } else if (isAdmin) {
    displayName = "Admin";
  } else {
    displayName = "Ayush";
  }

  // Quick Action Buttons
  const quickActions = useMemo(() => {
    if (isAdmin) {
      return [
        { label: "Add Property", icon: <FiPlus />, path: "/property/add-property", primary: true },
        { label: "Create Post", icon: <FiFileText />, path: "/post/add-post" },
        { label: "Business Tools", icon: <FiGrid />, path: "/admin/tools" },
        { label: "Lease Catalog", icon: <FiKey />, path: "/lease" },
      ];
    }
    if (isPartner) {
      return [
        { label: "Partner Leads", icon: <FiFileText />, path: `${routeBase}/leads`, primary: true },
        { label: "Add Listing", icon: <FiPlus />, path: "/add-property" },
        { label: "Current Plan", icon: <FiZap />, path: `${routeBase}/subscription` },
        { label: "Business Tools", icon: <FiGrid />, path: `${routeBase}/tools` },
        { label: "Shared Leads", icon: <FiCompass />, path: `${routeBase}/shared-leads` },
      ];
    }
    return [
      { label: "Customer Leads", icon: <FiFileText />, path: `${routeBase}/leads`, primary: true },
      { label: "Home Services", icon: <FiGrid />, path: `${routeBase}/services` },
      { label: "Local Bazaar", icon: <FiShoppingBag />, path: `${routeBase}/bazaar` },
      { label: "Communities", icon: <FiUsers />, path: `${routeBase}/communities` },
      { label: "Rewards & Points", icon: <FiGift />, path: `${routeBase}/rewards` },
    ];
  }, [isAdmin, isPartner, routeBase]);

  const { Id } = useParams();
  if (Id) {
    return <Outlet />;
  }

  // Strictly separate Customer and Partner Dashboard experiences
  if (isPartner) {
    return <PartnerDashboardView />;
  }

  if (!isAdmin) {
    return <CustomerDashboardView />;
  }

  return (
    <div className="saas-dashboard-root">
      {/* 1. Welcome / Overview Banner */}
      <div className="saas-dashboard-welcome">
        <div className="saas-dashboard-welcome__left">
          <h1 className="saas-dashboard-welcome__title">
            {greeting}, {displayName}!
          </h1>
          <p className="saas-dashboard-welcome__subtitle">
            Welcome to your All Home Desire workspace. Monitor recent verified properties, inquiries, and market posts.
          </p>
        </div>
        <div className="saas-dashboard-welcome__actions">
          {quickActions.map((action, idx) => (
            <button
              key={idx}
              type="button"
              className={`saas-btn ${action.primary ? "saas-btn--primary" : "saas-btn--secondary"}`}
              onClick={() => navigate(action.path)}
            >
              {action.icon}
              <span>{action.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* 2. Real KPI / Summary Cards */}
      {loading ? (
        <SaaSLoadingSkeleton type="stats" count={4} />
      ) : (
        <div className="row g-3 mb-4">
          <div className="col-12 col-sm-6 col-lg-3">
            <SaaSStatCard
              title="Nearby Properties"
              value={properties.length}
              icon={<FiHome />}
              color="blue"
              subtitle="Verified Active Listings"
              onClick={() => navigate(`${routeBase}/properties`)}
            />
          </div>
          <div className="col-12 col-sm-6 col-lg-3">
            <SaaSStatCard
              title="Community Posts"
              value={posts.length}
              icon={<FiMessageCircle />}
              color="green"
              subtitle="Recent Market Inquiries"
              onClick={() => navigate(`${routeBase}/posts`)}
            />
          </div>
          <div className="col-12 col-sm-6 col-lg-3">
            <SaaSStatCard
              title="Promotions & Deals"
              value={promotions.length}
              icon={<FiTag />}
              color="amber"
              subtitle="Special Partner Offers"
              onClick={() => navigate(`${routeBase}/promotions`)}
            />
          </div>
          <div className="col-12 col-sm-6 col-lg-3">
            <SaaSStatCard
              title="Service Tools"
              value="4 Ready"
              icon={<FiCompass />}
              color="purple"
              subtitle="Ledgers, Leases & Cash"
              onClick={() => navigate(`${routeBase}/tools`)}
            />
          </div>
        </div>
      )}

      {/* 2.1 Customer Mobile Features Quick Access Grid */}
      {!isAdmin && !isPartner && (
        <div className="saas-dashboard-section mb-4">
          <div className="card border-0 shadow-sm rounded-3 p-3">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <div>
                <h6 className="fw-bold text-dark mb-0 d-flex align-items-center gap-2">
                  <FiGrid className="text-primary" /> Customer Services & Community Hub
                </h6>
                <small className="text-muted">Direct access to mobile customer features</small>
              </div>
            </div>
            <div className="row g-2">
              <div className="col-6 col-md-4 col-lg-2">
                <button
                  className="btn btn-outline-light text-dark border w-100 p-3 rounded-3 d-flex flex-column align-items-center justify-content-center gap-2 transition-all hover-shadow bg-white"
                  onClick={() => navigate(`${routeBase}/services`)}
                >
                  <div className="rounded-circle bg-primary bg-opacity-10 text-primary p-2">
                    <FiGrid size={20} />
                  </div>
                  <span className="fw-semibold small text-nowrap">Home Services</span>
                </button>
              </div>
              <div className="col-6 col-md-4 col-lg-2">
                <button
                  className="btn btn-outline-light text-dark border w-100 p-3 rounded-3 d-flex flex-column align-items-center justify-content-center gap-2 transition-all hover-shadow bg-white"
                  onClick={() => navigate(`${routeBase}/bazaar`)}
                >
                  <div className="rounded-circle bg-success bg-opacity-10 text-success p-2">
                    <FiShoppingBag size={20} />
                  </div>
                  <span className="fw-semibold small text-nowrap">Local Bazaar</span>
                </button>
              </div>
              <div className="col-6 col-md-4 col-lg-2">
                <button
                  className="btn btn-outline-light text-dark border w-100 p-3 rounded-3 d-flex flex-column align-items-center justify-content-center gap-2 transition-all hover-shadow bg-white"
                  onClick={() => navigate(`${routeBase}/communities`)}
                >
                  <div className="rounded-circle bg-info bg-opacity-10 text-info p-2">
                    <FiUsers size={20} />
                  </div>
                  <span className="fw-semibold small text-nowrap">Communities</span>
                </button>
              </div>
              <div className="col-6 col-md-4 col-lg-2">
                <button
                  className="btn btn-outline-light text-dark border w-100 p-3 rounded-3 d-flex flex-column align-items-center justify-content-center gap-2 transition-all hover-shadow bg-white"
                  onClick={() => navigate(`${routeBase}/rewards`)}
                >
                  <div className="rounded-circle bg-warning bg-opacity-10 text-warning p-2">
                    <FiGift size={20} />
                  </div>
                  <span className="fw-semibold small text-nowrap">Rewards & Loyalty</span>
                </button>
              </div>
              <div className="col-6 col-md-4 col-lg-2">
                <button
                  className="btn btn-outline-light text-dark border w-100 p-3 rounded-3 d-flex flex-column align-items-center justify-content-center gap-2 transition-all hover-shadow bg-white"
                  onClick={() => navigate(`${routeBase}/reminders`)}
                >
                  <div className="rounded-circle bg-danger bg-opacity-10 text-danger p-2">
                    <FiCalendar size={20} />
                  </div>
                  <span className="fw-semibold small text-nowrap">Reminders</span>
                </button>
              </div>
              <div className="col-6 col-md-4 col-lg-2">
                <button
                  className="btn btn-outline-light text-dark border w-100 p-3 rounded-3 d-flex flex-column align-items-center justify-content-center gap-2 transition-all hover-shadow bg-white"
                  onClick={() => navigate(`${routeBase}/healthians`)}
                >
                  <div className="rounded-circle bg-secondary bg-opacity-10 text-secondary p-2">
                    <FiActivity size={20} />
                  </div>
                  <span className="fw-semibold small text-nowrap">Care Leads</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 2.2 Partner Role Mobile Features Quick Access Grid */}
      {isPartner && (
        <div className="saas-dashboard-section mb-4">
          <div className="card border-0 shadow-sm rounded-3 p-3">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <div>
                <h6 className="fw-bold text-dark mb-0 d-flex align-items-center gap-2">
                  <FiZap className="text-warning" /> Partner Offerings & Inquiries Hub
                </h6>
                <small className="text-muted">Manage your verified customer inquiries, catalogs, and store</small>
              </div>
            </div>
            <div className="row g-2">
              <div className="col-6 col-md-4 col-lg-2">
                <button
                  className="btn btn-outline-light text-dark border w-100 p-3 rounded-3 d-flex flex-column align-items-center justify-content-center gap-2 transition-all hover-shadow bg-white"
                  onClick={() => navigate(`${routeBase}/leads`)}
                >
                  <div className="rounded-circle bg-primary bg-opacity-10 text-primary p-2">
                    <FiFileText size={20} />
                  </div>
                  <span className="fw-semibold small text-nowrap">Partner Leads</span>
                </button>
              </div>
              <div className="col-6 col-md-4 col-lg-2">
                <button
                  className="btn btn-outline-light text-dark border w-100 p-3 rounded-3 d-flex flex-column align-items-center justify-content-center gap-2 transition-all hover-shadow bg-white"
                  onClick={() => navigate(`${routeBase}/interior-design`)}
                >
                  <div className="rounded-circle bg-info bg-opacity-10 text-info p-2">
                    <FiLayers size={20} />
                  </div>
                  <span className="fw-semibold small text-nowrap">Interior Catalogs</span>
                </button>
              </div>
              <div className="col-6 col-md-4 col-lg-2">
                <button
                  className="btn btn-outline-light text-dark border w-100 p-3 rounded-3 d-flex flex-column align-items-center justify-content-center gap-2 transition-all hover-shadow bg-white"
                  onClick={() => navigate(`${routeBase}/local-bazaar`)}
                >
                  <div className="rounded-circle bg-success bg-opacity-10 text-success p-2">
                    <FiShoppingBag size={20} />
                  </div>
                  <span className="fw-semibold small text-nowrap">Local Bazaar</span>
                </button>
              </div>
              <div className="col-6 col-md-4 col-lg-2">
                <button
                  className="btn btn-outline-light text-dark border w-100 p-3 rounded-3 d-flex flex-column align-items-center justify-content-center gap-2 transition-all hover-shadow bg-white"
                  onClick={() => navigate(`${routeBase}/services`)}
                >
                  <div className="rounded-circle bg-purple bg-opacity-10 text-purple p-2" style={{ color: "#6f42c1" }}>
                    <FiGrid size={20} />
                  </div>
                  <span className="fw-semibold small text-nowrap">My Services</span>
                </button>
              </div>
              <div className="col-6 col-md-4 col-lg-2">
                <button
                  className="btn btn-outline-light text-dark border w-100 p-3 rounded-3 d-flex flex-column align-items-center justify-content-center gap-2 transition-all hover-shadow bg-white"
                  onClick={() => navigate(`${routeBase}/reminders`)}
                >
                  <div className="rounded-circle bg-danger bg-opacity-10 text-danger p-2">
                    <FiCalendar size={20} />
                  </div>
                  <span className="fw-semibold small text-nowrap">Reminders</span>
                </button>
              </div>
              <div className="col-6 col-md-4 col-lg-2">
                <button
                  className="btn btn-outline-light text-dark border w-100 p-3 rounded-3 d-flex flex-column align-items-center justify-content-center gap-2 transition-all hover-shadow bg-white"
                  onClick={() => navigate(`${routeBase}/subscription`)}
                >
                  <div className="rounded-circle bg-warning bg-opacity-10 text-warning p-2">
                    <FiZap size={20} />
                  </div>
                  <span className="fw-semibold small text-nowrap">Current Plan</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 3. Recent Properties Section */}
      <div className="saas-dashboard-section mb-4">
        <SaaSSectionHeader
          title="Recent Properties & Listings"
          subtitle="Explore recently published properties across your preferred regions"
          linkTo={`${routeBase}/properties`}
          linkLabel="View All Properties"
          badge={properties.length > 0 ? `${properties.length} Available` : null}
        />

        {loading ? (
          <SaaSLoadingSkeleton type="card" count={3} />
        ) : properties.length > 0 ? (
          <div className="saas-cards-horizontal-scroll">
            {properties.map((prop, idx) => (
              <PropertyCard
                key={prop.id || idx}
                item={prop}
                assetUrl={assetUrl}
                routeBase={routeBase}
                navigate={navigate}
              />
            ))}
          </div>
        ) : (
          <SaaSEmptyState
            icon={<FiHome />}
            title="No Properties Available"
            description="There are currently no active properties in your area. Add a property listing to get started."
            actionLabel="Add New Property"
            actionLink="/add-property"
          />
        )}
      </div>

      {/* 4. Recent Community Posts Section */}
      <div className="saas-dashboard-section mb-4">
        <SaaSSectionHeader
          title="Recent Community Posts & Inquiries"
          subtitle="Latest client requirements, questions, and market broadcasts"
          linkTo={`${routeBase}/posts`}
          linkLabel="View All Posts"
          badge={posts.length > 0 ? `${posts.length} Posts` : null}
        />

        {loading ? (
          <SaaSLoadingSkeleton type="card" count={3} />
        ) : posts.length > 0 ? (
          <div className="saas-cards-horizontal-scroll">
            {posts.map((post, idx) => (
              <PostCard
                key={post.id || idx}
                item={post}
                routeBase={routeBase}
                navigate={navigate}
              />
            ))}
          </div>
        ) : (
          <SaaSEmptyState
            icon={<FiFileText />}
            title="No Community Posts Yet"
            description="Share your real estate requirements or services with verified partners and customers."
            actionLabel="Create First Post"
            actionLink="/post/add-post"
          />
        )}
      </div>

      {/* 5. Active Promotions Section */}
      <div className="saas-dashboard-section mb-4">
        <SaaSSectionHeader
          title="Featured Promotions & Offers"
          subtitle="Exclusive partner discounts and prime promotional campaigns"
          linkTo={`${routeBase}/promotions`}
          linkLabel="View All Offers"
        />

        {loading ? (
          <SaaSLoadingSkeleton type="card" count={3} />
        ) : promotions.length > 0 ? (
          <div className="saas-cards-horizontal-scroll">
            {promotions.map((promo, idx) => (
              <PromoCard
                key={promo.id || idx}
                item={promo}
                assetUrl={assetUrl}
                routeBase={routeBase}
                navigate={navigate}
              />
            ))}
          </div>
        ) : (
          <SaaSEmptyState
            icon={<FiTag />}
            title="No Active Promotions"
            description="Check back soon for seasonal discounts, partner benefits, and featured campaigns."
          />
        )}
      </div>
    </div>
  );
};

export default MainDashboard;
